// PermissionsEditor.js
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import './PermissionsEditor.css';

const InlineEditor = ({ value, onSave, placeholder = '' }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);

  useEffect(() => setText(value), [value]);

  const save = () => {
    const trimmed = text.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  };

  return editing ? (
    <span className="ie-container">
      <input
        className="ie-input"
        value={text}
        placeholder={placeholder}
        onChange={e => setText(e.target.value)}
        onBlur={save}
        onKeyDown={e => e.key === 'Enter' && save()}
        autoFocus
      />
      <button className="ie-cancel" onClick={() => setEditing(false)}>✕</button>
    </span>
  ) : (
    <span className="ie-text" onClick={() => setEditing(true)}>
      {value || <i>{placeholder}</i>}
    </span>
  );
};

const PermissionsEditor = observer(({ adminStore }) => {
  const [columns, setColumns] = useState({ server: [], client: [] });
  const [newServerName, setNewServerName] = useState('');
  const [newServerDesc, setNewServerDesc] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newClientDesc, setNewClientDesc] = useState('');
  const [deletingIds, setDeletingIds] = useState([]);

  useEffect(() => {
    adminStore.fetchRolesAndPermissions();
  }, [adminStore]);

  useEffect(() => {
    const srv = [];
    const cli = [];
    adminStore.permissions.forEach(p => {
      p.target === 'client' ? cli.push(p) : srv.push(p);
    });
    setColumns({ server: srv, client: cli });
  }, [adminStore.permissions]);

  const handleMove = (id, toTarget) => {
    adminStore.updatePermission(id, { target: toTarget });
  };

  const handleDelete = async id => {
    if (deletingIds.includes(id)) return;
    setDeletingIds(prev => [...prev, id]);
    try {
      await adminStore.deletePermission(id);
    } catch (err) {
      console.error('Failed to delete permission', err);
    } finally {
      setDeletingIds(prev => prev.filter(pid => pid !== id));
    }
  };

  const handleAddServer = () => {
    if (!newServerName.trim()) return;
    adminStore.createPermission({ name: newServerName.trim(), description: newServerDesc.trim(), target: 'server' });
    setNewServerName('');
    setNewServerDesc('');
  };

  const handleAddClient = () => {
    if (!newClientName.trim()) return;
    adminStore.createPermission({ name: newClientName.trim(), description: newClientDesc.trim(), target: 'client' });
    setNewClientName('');
    setNewClientDesc('');
  };

  return (
    <div className="pe-container">
      <h1>Редактор прав</h1>
      <div className="pe-columns">
        {['server', 'client'].map(target => (
          <div key={target} className="pe-column">
            <h2 className="pe-col-title">{target === 'server' ? 'Server' : 'Client'}</h2>

            <div className="pe-add-inline">
              <input
                className="pe-input"
                type="text"
                placeholder="Название"
                value={target === 'server' ? newServerName : newClientName}
                onChange={e => target === 'server' ? setNewServerName(e.target.value) : setNewClientName(e.target.value)}
              />
              <input
                className="pe-input"
                type="text"
                placeholder="Комментарий"
                value={target === 'server' ? newServerDesc : newClientDesc}
                onChange={e => target === 'server' ? setNewServerDesc(e.target.value) : setNewClientDesc(e.target.value)}
              />
              <button className="pe-add-btn" onClick={target === 'server' ? handleAddServer : handleAddClient}>
                Добавить
              </button>
            </div>

            <ul className="pe-list">
              {columns[target].map(p => (
                <li key={p.id} className="pe-item">
                  <InlineEditor
                    value={p.name}
                    placeholder="Название"
                    onSave={text => adminStore.updatePermission(p.id, { name: text })}
                  />
                  <InlineEditor
                    value={p.description}
                    placeholder="Комментарий"
                    onSave={text => adminStore.updatePermission(p.id, { description: text })}
                  />
                  <button className="pe-move" onClick={() => handleMove(p.id, target === 'server' ? 'client' : 'server')}>
                    {target === 'server' ? 'На клиент' : 'На сервер'}
                  </button>
                  <button
                    className="pe-delete"
                    disabled={deletingIds.includes(p.id)}
                    onClick={() => handleDelete(p.id)}
                  >
                    {deletingIds.includes(p.id) ? 'Удаление...' : 'Удалить'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
});

export default PermissionsEditor;